import { ArrowDown, ArrowUp, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useNotify, useRefresh, useUpdate } from "ra-core";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { SectionCard } from "../data-display/SectionCard";
import { DeleteFieldButton, formsInputClass } from "./FormsPage";
import {
  FIELD_TYPE_CHOICES,
  slugify,
  type FormFieldDef,
  type LeadForm,
} from "./formModel";

/**
 * Field list editor. Edits are local until "Save fields" writes the whole
 * jsonb array back, a small, predictable builder rather than a canvas.
 */
export const FormFieldsEditor = ({ form }: { form: LeadForm }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isPending }] = useUpdate();
  const [fields, setFields] = useState<FormFieldDef[]>(form.fields);

  // Re-sync local state when another form is selected.
  useEffect(() => setFields(form.fields), [form.id, form.fields]);

  const patch = (index: number, changes: Partial<FormFieldDef>) =>
    setFields((prev) =>
      prev.map((field, i) => (i === index ? { ...field, ...changes } : field)),
    );

  const move = (index: number, delta: number) =>
    setFields((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const addField = () =>
    setFields((prev) => [
      ...prev,
      { key: `field_${prev.length + 1}`, label: "New field", type: "text" },
    ]);

  const save = () => {
    for (const field of fields) {
      if (!field.key.trim()) {
        notify("Every field needs a key", { type: "warning" });
        return;
      }
    }
    const keys = fields.map((f) => f.key);
    if (new Set(keys).size !== keys.length) {
      notify("Field keys must be unique", { type: "warning" });
      return;
    }
    update(
      "forms",
      { id: form.id, data: { fields }, previousData: form },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify("Fields saved");
          refresh();
        },
        onError: (error: unknown) =>
          notify(
            error instanceof Error ? error.message : "Could not save fields",
            { type: "error" },
          ),
      },
    );
  };

  return (
    <SectionCard
      title="Fields"
      subtitle="What visitors fill in, consent checkboxes feed the contact's consent state"
    >
      <div className="flex flex-col gap-2">
        {fields.map((field, index) => (
          <Card key={index} className="py-0">
            <CardContent className="flex flex-col gap-2 p-3">
              <div className="flex items-center gap-2">
                <input
                  className={formsInputClass}
                  value={field.label ?? ""}
                  placeholder="Label"
                  onChange={(e) =>
                    patch(index, {
                      label: e.target.value,
                      key:
                        field.key.startsWith("field_") ||
                        field.key === slugify(field.label ?? "")
                          ? slugify(e.target.value) || field.key
                          : field.key,
                    })
                  }
                />
                <select
                  className={formsInputClass + " w-36 min-w-36"}
                  value={field.type}
                  onChange={(e) =>
                    patch(index, {
                      type: e.target.value as FormFieldDef["type"],
                    })
                  }
                >
                  {FIELD_TYPE_CHOICES.map((choice) => (
                    <option key={choice.id} value={choice.id}>
                      {choice.name}
                    </option>
                  ))}
                </select>
                <label className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                  <input
                    type="checkbox"
                    className="size-3.5 accent-primary"
                    checked={field.required ?? false}
                    onChange={(e) =>
                      patch(index, { required: e.target.checked })
                    }
                  />
                  Req.
                </label>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  onClick={() => move(index, -1)}
                >
                  <ArrowUp className="size-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  onClick={() => move(index, 1)}
                >
                  <ArrowDown className="size-3.5" />
                </Button>
                <DeleteFieldButton
                  onClick={() =>
                    setFields((prev) => prev.filter((_, i) => i !== index))
                  }
                />
              </div>
              {field.type === "select" ? (
                <input
                  className={formsInputClass}
                  placeholder="Options, comma-separated"
                  value={(field.options ?? []).join(", ")}
                  onChange={(e) =>
                    patch(index, {
                      options: e.target.value
                        .split(",")
                        .map((o) => o.trim())
                        .filter(Boolean),
                    })
                  }
                />
              ) : null}
            </CardContent>
          </Card>
        ))}
        <div className="mt-1 flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={addField}>
            <Plus className="mr-1 size-3.5" />
            Add field
          </Button>
          <Button size="sm" disabled={isPending} onClick={save}>
            Save fields
          </Button>
        </div>
      </div>
    </SectionCard>
  );
};
